import type { Incident, IncidentOwn } from '@travel-planner/shared/schemas';
import { useAuthData } from '@auth/features/auth';
import {
  buildReference,
  IncidentSeverityBadge,
  IncidentStatusBadge,
  useIncidentDrawerStore,
  useMyIncidents,
} from '@incidents/entities/incident';
import { ReportIncidentModal } from '@incidents/features/incident-form';
import { useNavigate } from '@tanstack/react-router';
import { Plus, TriangleAlert } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Button, EmptyState, Skeleton, SlidePanel, Text } from '@/shared/ui';
import css from './incident-drawer.module.scss';

// Compact "My Incidents" card. buildReference gives the entity Reference; the
// link navigates to the stored full URL (exact original page incl. query).
const IncidentRow = ({
  inc,
  onNavigate,
}: {
  inc: IncidentOwn | Incident;
  onNavigate: () => void;
}) => {
  const reference = buildReference(inc);
  return (
    <li className={css.item}>
      <Text weight="medium">{inc.whatHappened}</Text>
      <div className={css.itemMeta}>
        <IncidentSeverityBadge severity={inc.severity} />
        <IncidentStatusBadge status={inc.status} />
        {reference.url ? (
          <a className={css.refLink} href={reference.url} onClick={onNavigate}>
            {reference.label}
          </a>
        ) : (
          <Text as="span" size="xs" variant="muted">
            {reference.label}
          </Text>
        )}
        <Text as="span" size="xs" variant="muted">
          {/* Date + time — same "YYYY-MM-DD HH:MM" format as the All
              Incidents table (UTC wall-clock ISO, sliced). */}
          {inc.createdOn.slice(0, 16).replace('T', ' ')}
        </Text>
      </div>
    </li>
  );
};

const ListSkeleton = () => (
  <div className={css.list}>
    {Array.from({ length: 4 }, (_, i) => `skeleton-${i}`).map((id) => (
      <div className={css.item} key={id}>
        <Skeleton width="70%" />
        <Skeleton width="40%" />
      </div>
    ))}
  </div>
);

// The reporter's own incidents — compact card list.
const MyIncidents = ({
  enabled,
  onNavigate,
}: {
  enabled: boolean;
  onNavigate: () => void;
}) => {
  const { data, isLoading } = useMyIncidents(enabled);
  const rows = data?.data ?? [];

  if (isLoading) return <ListSkeleton />;
  if (rows.length === 0) {
    return (
      <EmptyState
        description="Incidents you report will appear here."
        icon={<TriangleAlert style={{ height: 22, width: 22 }} />}
        title="No incidents yet"
      />
    );
  }
  return (
    <ul className={css.list}>
      {rows.map((inc) => (
        <IncidentRow inc={inc} key={inc.id} onNavigate={onNavigate} />
      ))}
    </ul>
  );
};

// Mounted once at the AppLayout root so it overlays every page. Shows ONLY the
// reporter's own incidents (compact cards). "All Incidents" is not a tab — it's
// an action that closes the drawer and navigates to the full management page
// (/incidents-management), shown only to users with incidents:read:any. The
// standalone page is not in the sidebar; this button is its only entry point.
export const IncidentDrawer = () => {
  const open = useIncidentDrawerStore((s) => s.open);
  const closeDrawer = useIncidentDrawerStore((s) => s.closeDrawer);
  const openReport = useIncidentDrawerStore((s) => s.openReport);
  const { data: authData } = useAuthData();
  const navigate = useNavigate();
  const canViewAll =
    authData?.user.permissions.includes('incidents:read:any') ?? false;

  const goToAllIncidents = () => {
    closeDrawer();
    navigate({ to: '/incidents-management' });
  };

  return (
    <>
      <SlidePanel
        onClose={closeDrawer}
        open={open}
        title="Incidents"
        width={720}
      >
        <div className={css.body}>
          <div className={css.toolbar}>
            {canViewAll ? (
              // biome-ignore lint/a11y/useSemanticElements: styled segmented control (My/All), not a form fieldset
              <div
                aria-label="Incident scope"
                className={css.segmented}
                role="group"
              >
                <button
                  aria-pressed="true"
                  className={joinCss(css.segment, css.segmentActive)}
                  type="button"
                >
                  My Incidents
                </button>
                <button
                  className={css.segment}
                  onClick={goToAllIncidents}
                  type="button"
                >
                  All Incidents
                </button>
              </div>
            ) : (
              <span />
            )}
            <div className={css.actions}>
              <Button
                icon={<Plus style={{ height: 14, width: 14 }} />}
                onClick={() => openReport()}
                size="sm"
                variant="primary"
              >
                Report incident
              </Button>
            </div>
          </div>

          <MyIncidents enabled={open} onNavigate={closeDrawer} />
        </div>
      </SlidePanel>

      <ReportIncidentModal />
    </>
  );
};
